import { updateUserSettingsAction } from '../../app/actions/UpdateUserSettingsAction';
import { appStore } from '../../app/AppStore';
import { IUserInfo } from '../models/IUserInfo';
import { Module } from '../Module';
import { ServerSettings } from '../resources/ServerSettings';
import { ServerUsers } from '../../userManagement/services/resources/ServerUsers';
import { UserSettings } from '../resources/UserSettings';
import { IGetUserSettingsResult } from '../ServerApi/requests/GetUserSettingsRequest';
import { UpdateSiteStartPageRequest } from '../ServerApi/requests/UpdateSiteStartPageRequest';
import { UpdateStringServerSettingRequest } from '../ServerApi/requests/UpdateStringServerSettingRequest';
import { IResult } from '../ServerApi/types/IResponse';
import { ConfirmActionDialog, IConfirmActionDialogInstance } from '../services/ConfirmActionDialog';
import { ModalSize } from '../services/ModalService';
import { ServerService } from '../services/ServerService';
import { StartPageService } from '../services/StartPageService';
import { ToasterService, ToastType } from '../services/ToasterService';

export class UserSettingsActions {
  private toaster = ToasterService.instance;
  public static $inject = [
    '$q',
    '$translate',
    'ConfirmActionDialog',
    'ServerService',
    'StartPageService',
    'UserSettings'
  ];

  constructor(
    private $q: ng.IQService,
    private $translate: ng.translate.ITranslateService,
    private confirmActionDialog: ConfirmActionDialog,
    private server: ServerService,
    private startPageService: StartPageService,
    private userSettings: UserSettings
  ) {}

  public confirmChangeUserStartPage() {
    this.confirmActionDialog.open({
      title: this.$translate.instant('userStartPageChange'),
      bodyText: this.$translate.instant('userStartPageConfirm'),
      actionName: this.$translate.instant('userStartPageButton'),
      action : (dialogInstance: IConfirmActionDialogInstance) => {
        return this.updateCurrentUsersStartPage(this.startPageService.getCurrentUrlAsStartPage())
          // TODO: move this error handling logic to the action or a store.
          .then((result: any) => {
            if (!result || result.errors) {
              dialogInstance.showErrorMessage(this.$translate.instant('userStartPageError'));
            }
          });
      }
    });
  }

  public confirmChangeSiteStartPage() {
    this.confirmActionDialog.open({
      title: this.$translate.instant('siteStartPageChange'),
      bodyText: this.$translate.instant('siteStartPageConfirm', {SITE_NAME: this.server.sessionSiteName()}),
      actionName: this.$translate.instant('siteStartPageButton'),
      size: ModalSize.fitToContent,
      action : (dialogInstance: IConfirmActionDialogInstance) => {
        return this.updateSiteStartPage(this.startPageService.getCurrentUrlAsStartPage())
          .then((result: any) => {
            if (!result || result.errors) {
              dialogInstance.showErrorMessage(this.$translate.instant('userStartPageError'));
            }
          });
      }
    });
  }

  public confirmChangeServerDefaultStartPage() {
    this.confirmActionDialog.open({
      title: this.$translate.instant('defaultStartPageChange'),
      bodyText: this.$translate.instant('defaultStartPageConfirm'),
      actionName: this.$translate.instant('defaultStartPageButton'),
      action : (dialogInstance: IConfirmActionDialogInstance) => {
        return this.server.sendRequest(new UpdateStringServerSettingRequest({
          setting: 'defaultStartPage',
          value: this.startPageService.getCurrentUrlAsStartPage()
        })).then((result: any) => {
          if (!result || result.errors) {
            dialogInstance.showErrorMessage(this.$translate.instant('defaultStartPageError'));
          } else {
            this.server.refreshSessionInfo().then(() => {
              this.toaster.pop({ type: ToastType.Info, text: this.$translate.instant('defaultStartPageSuccess') });
            });
          }
        });
      }
    });
  }

  public confirmResetServerDefaultStartPage(callback: (newStartPage: string) => void) {
    this.confirmActionDialog.open({
      title: this.$translate.instant('resetStartPage_dialog_title'),
      bodyText: this.$translate.instant('resetStartPage_dialog_message'),
      actionName: this.$translate.instant('defaultStartPageButton'),
      action: (dialogInstance: IConfirmActionDialogInstance) => {
        return this.server.sendRequest(new UpdateStringServerSettingRequest({
          setting: 'defaultStartPage',
          value: this.startPageService.getServerDefaultStartPage()
        })).then((result: any) => {
          if (!result || result.errors) {
            dialogInstance.showErrorMessage(this.$translate.instant('defaultStartPageError'));
          } else {
            this.toaster.pop({ type: ToastType.Info, text: this.$translate.instant('defaultStartPageSuccess') });
            if (callback) {
              callback(this.startPageService.getServerDefaultStartPage());
            }
          }
        });
      }
    });
  }

  /**
   * Resets the site start page.
   * Callback is called on success.
   */
  public confirmResetSiteDefaultStartPage(callback: () => void) {
    this.confirmActionDialog.open({
      title: this.$translate.instant('resetStartPage_dialog_title'),
      bodyText: this.$translate.instant('resetStartPage_dialog_message'),
      actionName: this.$translate.instant('siteStartPageButton'),
      action: (dialogInstance: IConfirmActionDialogInstance) => {
        return this.server.sendRequest(new UpdateSiteStartPageRequest({
          startPage: ''
        })).then((result: any) => {
          if (!result || result.errors) {
            dialogInstance.showErrorMessage(this.$translate.instant('resetSiteStartPageError'));
          } else {
            this.toaster.pop({ type: ToastType.Info, text: this.$translate.instant('defaultStartPageSuccess') });
            if (callback) {
              callback();
            }
          }
        });
      }
    });
  }

  private updateCurrentUsersStartPage(startPage: string) {
    // TODO: error handling for this action is still handled by callers. Fix that. Then there will be no need to return this promise.
    return this.userSettings.updateCurrentUsersStartPage(startPage)
      .then((result: IResult) => {
        if (result && !result.errors) {
          this.toaster.pop({ type: ToastType.Info, text: this.$translate.instant('userStartPageSuccess') });
          this.fetchUserSettings(this.server.sessionUsername(), this.server.sessionUserDomainName());
        }
        return result;
      });
  }

  private updateSiteStartPage(startPage: string) {
    return this.userSettings.updateSiteStartPage(startPage)
      .then((result: IResult) => {
        if (result && !result.errors) {
          this.toaster.pop({ type: ToastType.Info, text: this.$translate.instant('siteStartPageSuccess') });
          this.fetchUserSettings(this.server.sessionUsername(), this.server.sessionUserDomainName());
        }
        return result;
      });
  }

  public resetStartPage(user: IUserInfo): ng.IPromise<any> {
    // TODO: error handling for this action is still handled by callers. Fix that. Then there will be no need to return this promise.
    return this.userSettings.resetUserStartPage(user.id).then((result: IResult) => {
      if (result && !result.errors) {
        this.toaster.pop({ type: ToastType.Info, text: this.$translate.instant('resetStartPageSuccess') });
        this.fetchUserSettings(user.username, user.domainName);
      }
      return result;
    });
  }

  public fetchUserSettings(username: string, domain: string): ng.IPromise<IGetUserSettingsResult> {
    return ServerUsers.instance.getUserSettings(username, domain).then((result: IGetUserSettingsResult) => {
      // Some subscribers interpret the change to user settings as a signal that the session info has been updated,
      // so we ensure that it has before dispatching the update.
      // TODO: Somehow make this unnecessary?
      return this.maybeRefreshSessionInfo(username, domain).then(() => {
        appStore.dispatch(updateUserSettingsAction(username, domain, result));
        return result;
      });
    });
  }

  public forgetUserSettings(username: string, domain: string) {
    appStore.dispatch(updateUserSettingsAction(username, domain, undefined));
  }

  private maybeRefreshSessionInfo(username: string, domain: string): ng.IPromise<void> {
    if (username === this.server.sessionUsername() && domain === this.server.sessionUserDomainName()) {
      return this.server.refreshSessionInfo();
    } else {
      return this.$q.when();
    }
  }

}

Module.instance().service('UserSettingsActions', UserSettingsActions);
